// src/pages/SalesPage.js
import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from 'react';
import api from '../api/axios';
import { CartContext } from '../contexts/CartContext';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';
import { loadSettings, defaultSettings } from '../utils/posSettings';
import PhoneInput from '../components/PhoneInput';

/**
 * SalesPage implements the core POS interface. It shows a grid of products that
 * can be added to a cart by clicking or scanning a barcode. The cart uses a
 * shared CartContext which persists to localStorage. Users can apply a flat or
 * percentage discount, choose a payment method and print a receipt.
 */
const getReceiptPreferenceSnapshot = () => {
  if (typeof window === 'undefined') {
    return {
      print: defaultSettings.receiptPrintDefault,
      email: defaultSettings.receiptEmailDefault,
      rememberCustomer: defaultSettings.rememberCustomerDefault,
    };
  }

  const settings = loadSettings();
  return {
    print: settings.receiptPrintDefault ?? defaultSettings.receiptPrintDefault,
    email: settings.receiptEmailDefault ?? defaultSettings.receiptEmailDefault,
    rememberCustomer: settings.rememberCustomerDefault ?? defaultSettings.rememberCustomerDefault,
  };
};

const getStoredCustomerDetails = () => {
  if (typeof window === 'undefined') {
    return { name: '', email: '', phone: '' };
  }
  return {
    name: localStorage.getItem('pos-last-receipt-name') || '',
    email: localStorage.getItem('pos-last-receipt-email') || '',
    phone: localStorage.getItem('pos-last-receipt-phone') || '',
  };
};

export default function SalesPage({
  searchTerm: externalSearchTerm,
  onSearchTermChange,
  preorderMode = false,
}) {
  //  Products & UI state 
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const searchTerm =
    typeof externalSearchTerm === 'string' ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm =
    typeof onSearchTermChange === 'function' ? onSearchTermChange : setInternalSearchTerm;
  const currencyCode = useCurrencyCode();
  const normalizedSearchTerm = searchTerm.trim();

  // Colour palette for category buttons. Colours will cycle through this array.
  const categoryColors = [
    '#f87171', // red
    '#60a5fa', // blue
    '#fbbf24', // amber
    '#34d399', // green
    '#a78bfa', // purple
    '#fb923c', // orange
    '#f472b6', // pink
    '#38bdf8', // sky
  ];
  const [showReceipt, setShowReceipt] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPreorderModal, setShowPreorderModal] = useState(false);
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState(0);
  const [excludeDiscountedFromBasket, setExcludeDiscountedFromBasket] = useState(false);
  const [completedReceipt, setCompletedReceipt] = useState(null);
  const [showReceiptDetails, setShowReceiptDetails] = useState(false);
  const [receiptPreferences, setReceiptPreferences] = useState(getReceiptPreferenceSnapshot);
  const initialStoredCustomer = receiptPreferences.rememberCustomer ? getStoredCustomerDetails() : { name: '', email: '', phone: '' };
  const [receiptCustomerName, setReceiptCustomerName] = useState(initialStoredCustomer.name);
  const [receiptEmail, setReceiptEmail] = useState(initialStoredCustomer.email);
  const [receiptPhone, setReceiptPhone] = useState(initialStoredCustomer.phone);
  const [customerSelectQuery, setCustomerSelectQuery] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');
  const [savingCustomerPrefs, setSavingCustomerPrefs] = useState(false);
  const customerFetchTimeoutRef = useRef(null);
  const [printReceiptEnabled, setPrintReceiptEnabled] = useState(receiptPreferences.print);
  const [emailReceiptEnabled, setEmailReceiptEnabled] = useState(receiptPreferences.email);
  const [whatsappReceiptEnabled, setWhatsappReceiptEnabled] = useState(false);
  const [smsReceiptEnabled, setSmsReceiptEnabled] = useState(false);
  const [rememberCustomerNextSale, setRememberCustomerNextSale] = useState(receiptPreferences.rememberCustomer);
  const [sendingReceiptEmail, setSendingReceiptEmail] = useState(false);
  const [receiptDeliveryError, setReceiptDeliveryError] = useState('');
  const barcodeRef = useRef('');
  const [preorderSubmitting, setPreorderSubmitting] = useState(false);
  const [preorderCustomerCollapsed, setPreorderCustomerCollapsed] = useState(false);
  const [preorderAutoCollapsed, setPreorderAutoCollapsed] = useState(false);
  const [preorderForm, setPreorderForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    customerId: null,
  });

  //  Payment UI state 
  // Amount entered via keypad for tendering a payment. If empty, totalAmount will be used.
  const [paymentAmount, setPaymentAmount] = useState('');
  // Preset quick tender amounts for convenience
  const quickTenderAmounts = [5, 10, 20, 50];

  //  Held sales state 
  // List of held sales fetched from the backend
  const [heldSales, setHeldSales] = useState([]);
  // Control display of the held sales modal
  const [showHeldModal, setShowHeldModal] = useState(false);

  //  Misc Product (custom item) state 
  const [showMiscModal, setShowMiscModal] = useState(false);
  const [miscName, setMiscName] = useState('');
  const [miscPrice, setMiscPrice] = useState('');

  // Track multiple payment entries (e.g., Cash and Card). Each entry records method and amount.
  const [payments, setPayments] = useState([]);
  const [expandedCartItemId, setExpandedCartItemId] = useState(null);

  // Reset tender and payments whenever the payment modal opens
  useEffect(() => {
    if (showPaymentModal) {
      setPaymentAmount('');
      setPayments([]);
    }
  }, [showPaymentModal]);

  // Fetch held sales whenever the held modal is opened
  useEffect(() => {
    if (showHeldModal) {
      fetchHeldSales();
    }
  }, [showHeldModal]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleSettingsChange = () => {
      setReceiptPreferences(getReceiptPreferenceSnapshot());
    };
    window.addEventListener('pos-settings-changed', handleSettingsChange);
    return () => window.removeEventListener('pos-settings-changed', handleSettingsChange);
  }, []);

  const visibleProducts = useMemo(() => {
    const query = normalizedSearchTerm.toLowerCase();
    return products.filter((product) => {
      if (product.active === false) return false;
      if (selectedCategoryId === -1 && !product.isPopular) {
        return false;
      }
      if (
        selectedCategoryId !== null &&
        selectedCategoryId !== -1 &&
        product.categoryId !== selectedCategoryId
      ) {
        return false;
      }
      if (!query) return true;

      const fields = [
        product.name,
        product.sku,
        product.barcode,
        product.woocommerceSlug,
      ];

      return fields.some(
        (field) => field && field.toString().toLowerCase().includes(query)
      );
    });
  }, [products, selectedCategoryId, normalizedSearchTerm]);

  const applyReceiptDefaults = useCallback(() => {
    const stored = receiptPreferences.rememberCustomer ? getStoredCustomerDetails() : { name: '', email: '' };
    setPrintReceiptEnabled(receiptPreferences.print);
    setEmailReceiptEnabled(receiptPreferences.email);
    setWhatsappReceiptEnabled(false);
    setSmsReceiptEnabled(false);
    setRememberCustomerNextSale(receiptPreferences.rememberCustomer);
    setReceiptCustomerName(stored.name);
    setReceiptEmail(stored.email);
    setReceiptPhone(stored.phone);
    setCustomerSelectQuery('');
    setShowCustomerSuggestions(false);
    setActiveCustomer(null);
    setMarketingOptIn(false);
    setWhatsappOptIn(false);
    setSmsOptIn(false);
    setCustomerNotes('');
  }, [receiptPreferences]);

  useEffect(() => {
    if (!showReceipt) {
      applyReceiptDefaults();
    }
  }, [applyReceiptDefaults, showReceipt]);

  /**
   * Fetch list of held sales from the backend and update state.
   */
  const fetchHeldSales = async () => {
    try {
      const res = await api.get('/heldsales');
      setHeldSales(res.data);
    } catch (err) {
      console.error('Failed to load held sales:', err);
      toast.error('Error loading held sales');
    }
  };

  useEffect(() => {
    if (!showReceipt && !preorderMode) return;
    if (!customerSelectQuery || customerSelectQuery.length < 2) {
      setCustomerSuggestions([]);
      return;
    }
    if (customerFetchTimeoutRef.current) clearTimeout(customerFetchTimeoutRef.current);
    customerFetchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/customers', {
          params: { search: customerSelectQuery },
        });
        setCustomerSuggestions(res.data.rows || []);
      } catch (err) {
        console.error('Failed to search customers', err);
      }
    }, 300);
    return () => {
      if (customerFetchTimeoutRef.current) clearTimeout(customerFetchTimeoutRef.current);
    };
  }, [customerSelectQuery, showReceipt, preorderMode]);

  /**
   * Save the current cart as a held sale on the backend. Clears the cart on success.
   */
  const handleHold = async () => {
    if (cart.length === 0) {
      toast.error('Cannot hold an empty cart');
      return;
    }
    try {
      await api.post('/heldsales', { cartData: cart });
      clearCart();
      toast.success('Sale held successfully');
    } catch (err) {
      console.error('Failed to hold sale:', err);
      toast.error('Error holding sale');
    }
  };

  /**
   * Resume a held sale: load its cart data into the current cart and delete it from the backend.
   */
  const resumeSale = async (held) => {
    try {
      setCart(held.cartData);
      await api.delete(`/heldsales/${held.id}`);
      setShowHeldModal(false);
      toast.success('Held sale resumed');
    } catch (err) {
      console.error('Failed to resume sale:', err);
      toast.error('Error resuming held sale');
    }
  };

  /**
   * Delete a held sale without resuming. Updates local list on success.
   */
  const deleteHeldSale = async (held) => {
    try {
      await api.delete(`/heldsales/${held.id}`);
      setHeldSales((prev) => prev.filter((h) => h.id !== held.id));
      toast.success('Held sale deleted');
    } catch (err) {
      console.error('Failed to delete held sale:', err);
      toast.error('Error deleting held sale');
    }
  };

  /**
   * Add a custom misc product to the cart using the provided name and price. Validates
   * that name is provided and price is a positive number.
   */
  const handleAddMiscItem = () => {
    const price = parseFloat(miscPrice);
    if (!miscName.trim()) {
      toast.error('Item name is required');
      return;
    }
    if (isNaN(price) || price <= 0) {
      toast.error('Enter a valid price greater than 0');
      return;
    }
    const miscItem = {
      id: `misc-${Date.now()}`,
      name: miscName,
      price: price,
      priceInclTax: price,
      quantity: 1,
    };
    // Add to cart and reset misc inputs
    addToCart(miscItem);
    setMiscName('');
    setMiscPrice('');
    setShowMiscModal(false);
    toast.success('Custom item added');
  };

  /**
   * Compute the total amount for a held cart. Considers item-level discounts.
   * @param {Array} cartItems Array of product objects with quantity and optional discount fields.
   */
  const getHeldTotal = (cartItems) => {
    return cartItems.reduce((sum, item) => {
      const price = parseFloat(item.priceInclTax ?? item.price) || 0;
      const base = price * (item.quantity || 1);
      const discount = item.discount
        ? item.discountType === 'percent'
          ? (base * item.discount) / 100
          : item.discount
        : 0;
      return sum + (base - discount);
    }, 0);
  };

  // Append a digit or decimal point to the payment amount
  const appendDigit = (digit) => {
    // Prevent multiple decimals
    if (digit === '.' && paymentAmount.includes('.')) return;
    setPaymentAmount((prev) => prev + digit);
  };
  // Remove the last character from the payment amount
  const deleteLastDigit = () => setPaymentAmount((prev) => prev.slice(0, -1));
  // Set the payment amount to a quick tender value
  const setQuickAmount = (amt) => setPaymentAmount(String(amt));

  /**
   * Handle finalising payment. If the amount tendered is less than the total due, an
   * error is shown and the receipt is not saved. If the amount is more, a success
   * message is displayed with the change due before completing the payment.
   *
   * @param {string} method Payment method label (Cash, Card, Other)
   */
  const handlePayment = (method) => {
    // Calculate how much has already been paid and how much remains
    const amountPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const remainingDue = totalAmount - amountPaid;

    // Determine the tendered amount for this payment (default to remaining if empty)
    const tendered = paymentAmount === '' ? remainingDue : parseFloat(paymentAmount);
    if (isNaN(tendered) || tendered <= 0) {
      toast.error('Invalid payment amount');
      return;
    }
    // If tendered amount covers only part of the remaining due, record it and continue
    if (tendered < remainingDue) {
      setPayments((prev) => [...prev, { method, amount: tendered }]);
      setPaymentAmount('');
      const newRemaining = remainingDue - tendered;
      toast.success(
        `Received ${formatCurrency(tendered, currencyCode)} via ${method}. ${formatCurrency(
          newRemaining,
          currencyCode
        )} still due.`
      );
      return;
    }
    // Tendered amount is enough to complete the sale
    const newPayments = [...payments, { method, amount: tendered }];
    const change = tendered - remainingDue;
    const summary = newPayments
      .map((p) => `${p.method}: ${formatCurrency(p.amount, currencyCode)}`)
      .join(', ');
    const paymentSummary =
      change > 0
        ? `${summary}. Change: ${formatCurrency(change, currencyCode)}`
        : summary;
    if (change > 0) {
      toast.success(`Change due: ${formatCurrency(change, currencyCode)}`);
    }
    setPaymentAmount('');
    setPayments([]);
    completePayment(method, paymentSummary);
  };

  //  Item-level modifiers 
  const [currentItem, setCurrentItem] = useState(null);
  const [showItemDiscountModal, setShowItemDiscountModal] = useState(false);
  const [itemDiscountType, setItemDiscountType] = useState('percent');
  const [itemDiscountValue, setItemDiscountValue] = useState(0);
  const [showItemNoteModal, setShowItemNoteModal] = useState(false);
  const [itemNoteText, setItemNoteText] = useState('');

  //  Global cart state 
  const { cart, setCart } = useContext(CartContext);

  //  Fetch products once on mount and add barcode scanner listener 
  // Fetch categories and products once on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          api.get('/categories'),
          api.get('/products'),
        ]);
        setCategories(catRes.data);
        setProducts(prodRes.data);
      } catch (err) {
        console.error('Failed to load products or categories:', err);
        toast.error('Error loading products or categories');
      }
    };
    fetchData();
  }, []);

  // Listen for barcode scans and match against loaded products
  useEffect(() => {
    const handleKeyPress = (e) => {
      const char = e.key;
      if (/\d/.test(char)) {
        barcodeRef.current = (barcodeRef.current + char).slice(-13);
      }
      if (e.key === 'Enter' && barcodeRef.current) {
        const match = products.find((p) => p.barcode === barcodeRef.current);
        if (match) addToCart(match);
        barcodeRef.current = '';
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [products]);

  //  Cart manipulation helpers 
  const addToCart = (product) => {
    setCart((c) =>
      c.some((item) => item.id === product.id)
        ? c.map((item) =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        : [...c, { ...product, quantity: 1 }]
    );
  };
  const removeFromCart = (id) => setCart((c) => c.filter((item) => item.id !== id));
  const clearCart = () => setCart([]);
  const increaseQty = (id) =>
    setCart((c) =>
      c.map((item) =>
        item.id === id ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  const decreaseQty = (id) =>
    setCart((c) =>
      c
        .map((item) =>
          item.id === id ? { ...item, quantity: item.quantity - 1 } : item
        )
        .filter((item) => item.quantity > 0)
    );

  //  Item note & discount helpers 
  const openItemDiscountModal = (item) => {
    setCurrentItem(item);
    setItemDiscountType(item.discountType || 'percent');
    setItemDiscountValue(item.discount || 0);
    setShowItemDiscountModal(true);
  };
  const applyItemDiscount = () => {
    setCart((c) =>
      c.map((item) =>
        item.id === currentItem.id
          ? { ...item, discountType: itemDiscountType, discount: itemDiscountValue }
          : item
      )
    );
    setShowItemDiscountModal(false);
  };
  const openItemNoteModal = (item) => {
    setCurrentItem(item);
    setItemNoteText(item.note || '');
    setShowItemNoteModal(true);
  };
  const applyItemNote = () => {
    setCart((c) =>
      c.map((item) => (item.id === currentItem.id ? { ...item, note: itemNoteText } : item))
    );
    setShowItemNoteModal(false);
  };

  //  Totals & Discounts 
  const calculateSubtotal = (item) => {
    const price = parseFloat(item.priceInclTax ?? item.price) || 0;
    const base = price * item.quantity;
    const itemDiscount = item.discount
      ? item.discountType === 'percent'
        ? (base * item.discount) / 100
        : item.discount
      : 0;
    return base - itemDiscount;
  };
  // Total before any discounts (sum of base price * quantity)
  const totalBeforeDiscount = cart.reduce((sum, item) => {
    const price = parseFloat(item.priceInclTax ?? item.price) || 0;
    return sum + price * item.quantity;
  }, 0);
  // Sum of item-level discounts
  const itemDiscountSum = cart.reduce((sum, item) => {
    const price = parseFloat(item.priceInclTax ?? item.price) || 0;
    const base = price * item.quantity;
    const disc = item.discount
      ? item.discountType === 'percent'
        ? (base * item.discount) / 100
        : item.discount
      : 0;
    return sum + disc;
  }, 0);
  const basketEligibleSubtotal = cart.reduce((sum, item) => {
    const price = parseFloat(item.priceInclTax ?? item.price) || 0;
    if (excludeDiscountedFromBasket && item.discount) {
      return sum;
    }
    return sum + price * item.quantity;
  }, 0);
  // Basket-level discount (applied to the pre-discount total)
  const basketDiscount =
    discountType === 'percent'
      ? (basketEligibleSubtotal * discountValue) / 100
      : discountValue;
  // Combined total discount
  const totalDiscount = itemDiscountSum + basketDiscount;
  const totalAmount = totalBeforeDiscount - totalDiscount;

  const amountPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const currentTender = paymentAmount === '' ? 0 : parseFloat(paymentAmount) || 0;
  const totalTendered = amountPaid + currentTender;
  const remainingBalance = totalAmount - totalTendered;
  const taxTotal = cart.reduce((sum, item) => {
    const incl = parseFloat(item.priceInclTax ?? item.price) || 0;
    const excl = parseFloat(item.priceExclTax ?? item.price) || 0;
    return sum + (incl - excl) * item.quantity;
  }, 0);
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const categoryButtons = useMemo(
    () => [{ id: -1, name: 'Popular' }, ...categories],
    [categories]
  );
  const activeCategory =
    selectedCategoryId === -1
      ? { id: -1, name: 'Popular' }
      : selectedCategoryId
      ? categories.find((cat) => cat.id === selectedCategoryId)
      : null;
  const showingCategoryCards = !activeCategory && normalizedSearchTerm.length === 0;
  useEffect(() => {
    if (expandedCartItemId === null) return;
    if (!cart.some((item) => item.id === expandedCartItemId)) {
      setExpandedCartItemId(null);
    }
  }, [cart, expandedCartItemId]);

  const handleCartItemToggle = (itemId) => {
    setExpandedCartItemId((prev) => (prev === itemId ? null : itemId));
  };

  //  Checkout & Receipt 
  const handleCheckout = () => setShowPaymentModal(true);
  /**
   * Save the receipt in the backend. Accepts a payment method and an optional
   * summary string describing tender amounts and change. If paymentSummary is
   * omitted, the method name will be used. After successfully saving, the cart
   * is cleared and the receipt modal is shown.
   */
  const completePayment = async (method, paymentSummary) => {
    const summary = paymentSummary || method;
    const payload = {
      total: totalAmount,
      totalDiscount,
      paymentMethod: summary,
      items: cart.map((item) => ({
        productName: item.name,
        quantity: item.quantity,
        unitPrice: parseFloat(item.priceInclTax ?? item.price),
        discount: 0,
        subtotal: calculateSubtotal(item),
      })),
    };
    try {
      const res = await api.post('/receipts', payload);
      setCompletedReceipt(res.data);
      setShowPaymentModal(false);
      applyReceiptDefaults();
      setShowReceipt(true);
      clearCart();
    } catch (err) {
      console.error('Error saving receipt:', err);
      toast.error('Could not complete payment');
    }
  };

  const handlePrint = () => window.print();

  const closeReceiptModal = () => {
    setShowReceipt(false);
    setShowReceiptDetails(false);
    setReceiptDeliveryError('');
    applyReceiptDefaults();
  };

  const persistReceiptCustomer = async () => {
    if (!completedReceipt) return;
    const name = receiptCustomerName.trim();
    const email = receiptEmail.trim();
    const phone = receiptPhone.trim();
    if (!name && !email && !phone) return;
    try {
      await api.put(`/receipts/${completedReceipt.id}/customer`, {
        customerName: name || null,
        customerEmail: email || null,
        customerPhone: phone || null,
      });
      if (typeof window !== 'undefined') {
        if (rememberCustomerNextSale) {
          if (name) {
            localStorage.setItem('pos-last-receipt-name', name);
          } else {
            localStorage.removeItem('pos-last-receipt-name');
          }
          if (email) {
            localStorage.setItem('pos-last-receipt-email', email);
          } else {
            localStorage.removeItem('pos-last-receipt-email');
          }
          if (phone) {
            localStorage.setItem('pos-last-receipt-phone', phone);
          } else {
            localStorage.removeItem('pos-last-receipt-phone');
          }
        } else {
          localStorage.removeItem('pos-last-receipt-name');
          localStorage.removeItem('pos-last-receipt-email');
          localStorage.removeItem('pos-last-receipt-phone');
        }
      }
    } catch (err) {
      console.error('Failed to persist receipt contact', err);
      toast.error('Could not save customer details.');
    }
  };

  const handleReceiptDelivery = async () => {
    if (!completedReceipt) {
      toast.error('Receipt details not available.');
      return;
    }
    setReceiptDeliveryError('');

    await persistReceiptCustomer();

    const wantsPrint = printReceiptEnabled;
    const wantsEmail = emailReceiptEnabled;
    const wantsWhatsApp = whatsappReceiptEnabled;
    const wantsSms = smsReceiptEnabled;

    if (!wantsPrint && !wantsEmail && !wantsWhatsApp && !wantsSms) {
      toast.success('Sale recorded without receipt delivery.');
      closeReceiptModal();
      return;
    }

    if (wantsEmail) {
      const trimmedEmail = receiptEmail.trim();
      if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
        setReceiptDeliveryError('Enter a valid customer email address.');
        return;
      }
      setSendingReceiptEmail(true);
      try {
        await api.post(`/receipts/${completedReceipt.id}/email`, {
          email: trimmedEmail,
          customerName: receiptCustomerName.trim() || undefined,
        });
        toast.success('Receipt emailed to customer');
      } catch (err) {
        const message = err.response?.data?.error || 'Failed to email receipt';
        setReceiptDeliveryError(message);
        toast.error(message);
        setSendingReceiptEmail(false);
        return;
      }
      setSendingReceiptEmail(false);
    }

    if (wantsWhatsApp || wantsSms) {
      const trimmedPhone = receiptPhone.trim();
      if (!/^\+?[0-9]{7,15}$/.test(trimmedPhone)) {
        setReceiptDeliveryError('Enter a valid customer phone number with country code.');
        return;
      }
      const payload = {
        phone: trimmedPhone,
        customerName: receiptCustomerName.trim() || undefined,
        customerEmail: receiptEmail.trim() || undefined,
      };
      try {
        if (wantsWhatsApp) {
          await api.post(`/receipts/${completedReceipt.id}/whatsapp`, payload);
          toast.success('Receipt sent via WhatsApp.');
        }
        if (wantsSms) {
          await api.post(`/receipts/${completedReceipt.id}/sms`, payload);
          toast.success('Receipt sent via SMS.');
        }
      } catch (err) {
        const message = err.response?.data?.error || 'Failed to send messaging receipt';
        setReceiptDeliveryError(message);
        toast.error(message);
        return;
      }
    }

    if (wantsPrint) {
      handlePrint();
      toast.success('Receipt sent to printer');
    }

    closeReceiptModal();
  };

  const handleSkipReceiptDelivery = () => {
    toast.success('Sale completed without sending a receipt.');
    closeReceiptModal();
  };

  const handleReceiptEmailKeyDown = (event) => {
    if (event.key === 'Enter' && !sendingReceiptEmail) {
      event.preventDefault();
      handleReceiptDelivery();
    }
  };

  const getReceiptDeliveryCtaLabel = () => {
    const channels = [
      printReceiptEnabled && 'Print',
      emailReceiptEnabled && 'Email',
      whatsappReceiptEnabled && 'WhatsApp',
      smsReceiptEnabled && 'SMS',
    ].filter(Boolean);

    if (channels.length === 0) return 'Finish Sale';
    if (channels.length === 1) return `${channels[0]} Receipt`;
    return `Send (${channels.join(' + ')})`;
  };
  const applyDiscount = () => setShowDiscountModal(false);
  const handleSelectPreorderCustomer = (customer) => {
    if (!customer) return;
    setPreorderForm((prev) => ({
      ...prev,
      name: customer.name || prev.name,
      phone: customer.phone || prev.phone,
      email: customer.email || '',
      address: customer.address || '',
      notes: customer.notes || prev.notes,
      customerId: customer.id || null,
    }));
    setCustomerSelectQuery(customer.name || '');
    setShowCustomerSuggestions(false);
    setActiveCustomer(customer);
    setPreorderCustomerCollapsed(true);
    setPreorderAutoCollapsed(true);
  };

  const openPreorderModal = () => {
    if (cart.length === 0) {
      toast.error('Add items to the cart before creating a pre-order.');
      return;
    }
    setPreorderCustomerCollapsed(false);
    setPreorderAutoCollapsed(false);
    setPreorderForm((prev) => ({
      ...prev,
      name: receiptCustomerName || prev.name,
      phone: receiptPhone || prev.phone,
      email: receiptEmail || prev.email,
    }));
    setShowPreorderModal(true);
  };

  useEffect(() => {
    if (!preorderMode) return;
    if (preorderCustomerCollapsed || preorderAutoCollapsed) return;
    if (preorderForm.name.trim() && preorderForm.phone.trim()) {
      setPreorderCustomerCollapsed(true);
      setPreorderAutoCollapsed(true);
    }
  }, [
    preorderMode,
    preorderForm.name,
    preorderForm.phone,
    preorderCustomerCollapsed,
    preorderAutoCollapsed,
  ]);

  const submitPreorder = async (sendInvoice = false) => {
    if (cart.length === 0) {
      toast.error('Add items to the cart before creating a pre-order.');
      return;
    }
    if (!preorderForm.name.trim() || !preorderForm.phone.trim()) {
      toast.error('Customer name and phone are required for a pre-order.');
      return;
    }
    setPreorderSubmitting(true);
    try {
      const payload = {
        customerName: preorderForm.name.trim(),
        customerWhatsApp: preorderForm.phone.trim(),
        customerEmail: preorderForm.email.trim() || null,
        customerAddress: preorderForm.address.trim() || null,
        notes: preorderForm.notes.trim() || null,
        source: 'staff',
        customerId: preorderForm.customerId || null,
        products: cart.map((item) => ({
          productId: item.id,
          quantity: Number(item.quantity || 0),
        })),
      };
      const res = await api.post('/staff/preorders', payload);
      const createdId = res.data?.id || res.data?.preorder?.id;
      if (sendInvoice && createdId) {
        await api.post(`/preorders/${createdId}/send-invoice`);
      }
      toast.success(sendInvoice ? 'Pre-order saved and invoice sent.' : 'Pre-order saved from cart.');
      setShowPreorderModal(false);
      setPreorderSubmitting(false);
      clearCart();
      setPreorderForm({
        name: '',
        phone: '',
        email: '',
        address: '',
        notes: '',
        customerId: null,
      });
      setActiveCustomer(null);
      setCustomerSelectQuery('');
    } catch (err) {
      console.error('Failed to create pre-order', err);
      toast.error(err.response?.data?.error || 'Failed to create pre-order.');
      setPreorderSubmitting(false);
    }
  };

  return (
    <>
    <div className="flex h-full w-full overflow-hidden bg-slate-950 text-slate-100">
      <div className="flex h-[calc(100vh-4rem)] w-full flex-1 flex-col gap-4 px-4 pb-4 pt-2 lg:flex-row lg:gap-6">
        {/* Product canvas */}
        <section className="flex h-full flex-1 flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-inner">
          <div className="border-b border-slate-800 px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Browse</p>
                <h2 className="text-xl font-semibold text-white">Product Menu</h2>
              </div>
              <span className="text-xs font-medium text-slate-400">
                {visibleProducts.length} item{visibleProducts.length === 1 ? '' : 's'}
                {normalizedSearchTerm
                  ? ` matching "${normalizedSearchTerm}"`
                  : activeCategory
                  ? ` in ${activeCategory.name}`
                  : ''}
              </span>
            </div>
            {normalizedSearchTerm && (
              <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-slate-700 bg-slate-800/80 px-3 py-2 text-sm">
                <span>
                  Showing results for{' '}
                  <span className="font-semibold text-white">"{normalizedSearchTerm}"</span>
                </span>
                <button
                  onClick={() => setSearchTerm('')}
                  className="rounded-full border border-slate-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-400 hover:text-white"
                >
                  Clear
                </button>
              </div>
            )}
            <div className="-mx-1 mt-4 flex items-center gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => setSelectedCategoryId(null)}
                className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                  selectedCategoryId === null
                    ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                    : 'border-slate-700 bg-slate-800/60 text-slate-200 hover:border-slate-500'
                }`}
              >
                All Products
              </button>
              {categoryButtons.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategoryId(cat.id)}
                  className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                    selectedCategoryId === cat.id
                      ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200'
                      : 'border-slate-700 bg-slate-800/60 text-slate-200 hover:border-slate-500'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            {(showingCategoryCards ? categories.length : visibleProducts.length) === 0 ? (
              <div className="flex h-full items-center justify-center px-6 text-sm text-slate-400">
                No products found. Adjust your filters to continue selling.
              </div>
            ) : (
              <div className="h-full overflow-y-auto px-4 pb-6 pt-2">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                  {showingCategoryCards
                    ? categories.map((cat, idx) => {
                        const tone = categoryColors[idx % categoryColors.length];
                        const productCount = products.filter((p) => p.categoryId === cat.id).length;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => setSelectedCategoryId(cat.id)}
                            className="group relative flex h-40 flex-col justify-between rounded-xl border border-slate-800 bg-slate-800/70 p-4 text-left shadow-sm transition hover:border-emerald-400 hover:bg-slate-800"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-sm font-semibold text-white transition group-hover:text-emerald-300">
                                {cat.name}
                              </p>
                              <span
                                className="mt-1 inline-flex h-3 w-3 shrink-0 rounded-full"
                                style={{ backgroundColor: tone }}
                              />
                            </div>
                            <div className="text-xs uppercase tracking-wide text-slate-400">
                              Tap to view items
                            </div>
                            <div className="text-right text-sm font-semibold text-slate-200">
                              {productCount} product{productCount === 1 ? '' : 's'}
                            </div>
                          </button>
                        );
                      })
                    : visibleProducts.map((prod, idx) => {
                        const stockAccent = categoryColors[idx % categoryColors.length];
                        const unitPrice = parseFloat(prod.priceInclTax ?? prod.price) || 0;
                        return (
                          <button
                            key={prod.id}
                            onClick={() => addToCart(prod)}
                            className="group relative flex h-40 flex-col justify-between rounded-xl border border-slate-800 bg-slate-800/70 p-4 text-left shadow-sm transition hover:border-emerald-400 hover:bg-slate-800"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <p
                                className="text-sm font-semibold text-white transition group-hover:text-emerald-300"
                                title={prod.name}
                              >
                                {prod.name}
                              </p>
                              <span
                                className="mt-1 inline-flex h-3 w-3 shrink-0 rounded-full"
                                style={{ backgroundColor: stockAccent }}
                              />
                            </div>
                            <div className="mt-auto space-y-2 text-sm text-slate-300">
                              <div className="text-xs uppercase tracking-wide text-slate-500">SKU</div>
                              <div className="text-sm font-medium text-white">{prod.sku || 'N/A'}</div>
                            </div>
                            <div className="text-right text-lg font-semibold text-white">
                              {formatCurrency(unitPrice, currencyCode)}
                            </div>
                          </button>
                        );
                      })}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Cart / checkout rail */}
        <aside className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 lg:w-[32%] xl:w-[30%]">
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto">
              <div className="sticky top-0 z-20 border-b border-slate-800 bg-slate-900 px-4 py-4">
                <div className="flex items-center justify-between text-sm">
                  <h2 className="text-lg font-semibold text-white">
                    {preorderMode ? 'Pre-Order Cart' : 'Current Sale'}
                  </h2>
                  <span className="text-xs font-medium text-slate-400">
                    {totalItems} item{totalItems === 1 ? '' : 's'}
                  </span>
                </div>
                {preorderMode && (
                  <div className="mt-3 space-y-3 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Customer</p>
                        <p className="text-sm font-semibold text-white">
                          {preorderForm.name || 'Select or add customer'}
                        </p>
                        <p className="text-xs text-slate-400">
                          {preorderForm.phone || preorderForm.email || 'No contact added'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setPreorderCustomerCollapsed((val) => !val)}
                          className="rounded-full border border-slate-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-300 hover:border-slate-500"
                        >
                          {preorderCustomerCollapsed ? 'Edit' : 'Collapse'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPreorderForm({
                              name: '',
                              phone: '',
                              email: '',
                              address: '',
                              notes: '',
                              customerId: null,
                            });
                            setCustomerSelectQuery('');
                            setActiveCustomer(null);
                            setShowCustomerSuggestions(false);
                            setPreorderCustomerCollapsed(false);
                            setPreorderAutoCollapsed(false);
                          }}
                          className="rounded-full border border-slate-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-300 hover:border-slate-500"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    {!preorderCustomerCollapsed && (
                      <div className="space-y-2">
                        <label className="block text-xs uppercase tracking-[0.2em] text-slate-500">
                          Find existing
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={customerSelectQuery}
                            onChange={(e) => {
                              setCustomerSelectQuery(e.target.value);
                              setShowCustomerSuggestions(true);
                            }}
                            onFocus={() => setShowCustomerSuggestions(true)}
                            className="w-full rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                            placeholder="Search saved customers"
                          />
                          {showCustomerSuggestions && customerSuggestions.length > 0 && (
                            <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950 text-sm shadow-xl">
                              {customerSuggestions.map((customer) => (
                                <button
                                  type="button"
                                  key={customer.id}
                                  className="flex w-full flex-col gap-0.5 px-3 py-2 text-left text-slate-200 hover:bg-slate-900"
                                  onClick={() => handleSelectPreorderCustomer(customer)}
                                >
                                  <span className="font-semibold">{customer.name || 'Unnamed customer'}</span>
                                  <span className="text-[11px] text-slate-400">
                                    {customer.email || customer.phone || 'No contact info'}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          <label className="flex flex-col text-xs text-slate-400">
                            <span className="mb-1 uppercase tracking-[0.2em]">Name</span>
                            <input
                              type="text"
                              value={preorderForm.name}
                              onChange={(e) => setPreorderForm((f) => ({ ...f, name: e.target.value, customerId: null }))}
                              className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                            />
                          </label>
                          <PhoneInput
                            label="Phone / WhatsApp"
                            value={preorderForm.phone}
                            onChange={(value) => setPreorderForm((f) => ({ ...f, phone: value, customerId: null }))}
                            wrapperClassName="text-xs text-slate-400"
                            required
                            placeholder="e.g. 7900 999 000"
                          />
                          <label className="flex flex-col text-xs text-slate-400">
                            <span className="mb-1 uppercase tracking-[0.2em]">Email (optional)</span>
                            <input
                              type="email"
                              value={preorderForm.email}
                              onChange={(e) => setPreorderForm((f) => ({ ...f, email: e.target.value }))}
                              className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                            />
                          </label>
                          <label className="flex flex-col text-xs text-slate-400">
                            <span className="mb-1 uppercase tracking-[0.2em] text-slate-500">Address (optional)</span>
                            <input
                              type="text"
                              value={preorderForm.address}
                              onChange={(e) => setPreorderForm((f) => ({ ...f, address: e.target.value }))}
                              className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                            />
                          </label>
                        </div>
                        <label className="flex flex-col text-xs text-slate-400">
                          <span className="mb-1 uppercase tracking-[0.2em]">Notes (optional)</span>
                          <textarea
                            rows={2}
                            value={preorderForm.notes}
                            onChange={(e) => setPreorderForm((f) => ({ ...f, notes: e.target.value }))}
                            className="rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-3 grid grid-cols-[minmax(0,1fr)_64px_72px_80px] gap-2 text-[11px] uppercase tracking-wide text-slate-500">
                  <span>Product</span>
                  <span className="text-center">Qty</span>
                  <span className="text-right">Each</span>
                  <span className="text-right">Total</span>
                </div>
              </div>
              <ul className="space-y-3 px-2 py-4">
                {cart.length === 0 ? (
                  <li className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-6 text-center text-sm text-slate-400">
                    Scan or tap a product to begin the order.
                  </li>
                ) : (
                  cart.map((item) => {
                    const unitPrice = parseFloat(item.priceInclTax ?? item.price) || 0;
                    const lineTotal = calculateSubtotal(item);
                    const isExpanded = expandedCartItemId === item.id;
                    return (
                      <li
                        key={item.id}
                        className={`rounded-xl border border-slate-800 bg-slate-800/70 transition ${
                          isExpanded ? 'ring-2 ring-emerald-400/60' : 'hover:border-emerald-400/40'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => handleCartItemToggle(item.id)}
                          className="flex w-full flex-col gap-2 px-3 py-3 text-left"
                        >
                          <div className="grid grid-cols-[minmax(0,1fr)_64px_72px_80px] items-center gap-2 text-sm text-slate-200">
                            <div>
                              <p className="font-semibold text-white">{item.name}</p>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                                {item.note && (
                                  <span className="inline-flex items-center gap-1 rounded-full border border-slate-600 px-2 py-0.5 text-slate-200">
                                    Note added
                                  </span>
                                )}
                                {item.discount && (
                                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-amber-300">
                                    Discount {item.discount}
                                    {item.discountType === 'percent' ? '%' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className="text-center font-medium">{item.quantity}</span>
                            <span className="text-right text-sm text-slate-300">
                              {formatCurrency(unitPrice, currencyCode)}
                            </span>
                            <span className="text-right font-semibold text-white">
                              {formatCurrency(lineTotal, currencyCode)}
                            </span>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="flex flex-wrap items-center gap-2 border-t border-slate-700 px-3 py-3 text-xs">
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                openItemNoteModal(item);
                              }}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-3 py-1 text-slate-200 transition hover:border-emerald-400 hover:text-white"
                            >
                              <IconNote className="h-4 w-4" />
                              Note
                            </button>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                openItemDiscountModal(item);
                              }}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-3 py-1 text-slate-200 transition hover:border-emerald-400 hover:text-white"
                            >
                              <IconPercent className="h-4 w-4" />
                              Discount
                            </button>
                            <div className="ml-auto flex items-center gap-2 rounded-full border border-slate-600 bg-slate-800/80 px-2 py-1">
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  decreaseQty(item.id);
                                }}
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-lg font-semibold text-white transition hover:bg-slate-600"
                              >
                                -
                              </button>
                              <span className="w-8 text-center text-sm font-semibold text-white">
                                {item.quantity}
                              </span>
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  increaseQty(item.id);
                                }}
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-lg font-semibold text-white transition hover:bg-slate-600"
                              >
                                +
                              </button>
                            </div>
                            <button
                              onClick={(event) => {
                                event.stopPropagation();
                                removeFromCart(item.id);
                              }}
                              className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-3 py-1 text-red-300 transition hover:border-red-400 hover:text-red-200"
                            >
                              <IconTrash className="h-4 w-4" />
                              Remove
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 bg-slate-900 px-4 py-4 shadow-[0_-8px_24px_rgba(15,23,42,0.45)]">
            <div className="grid gap-4 text-sm text-slate-300 md:grid-cols-2">
              <div className="space-y-2">
                <div className="flex justify-between text-slate-400">
                  <span>Items</span>
                  <span>{totalItems}</span>
                </div>
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatCurrency(totalBeforeDiscount, currencyCode)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-amber-300">
                  <span>Total discount</span>
                  <span>-{formatCurrency(totalDiscount, currencyCode)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Tax</span>
                  <span>{formatCurrency(taxTotal, currencyCode)}</span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-between rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-lg font-semibold text-white">
              <span>Total due</span>
              <span>{formatCurrency(totalAmount, currencyCode)}</span>
            </div>
            <div className="mt-4 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={handleHold}
                  className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:border-amber-400 hover:text-amber-200"
                >
                  Hold
                </button>
                <button
                  onClick={() => setShowHeldModal(true)}
                  className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-400 hover:text-white"
                >
                  Resume
                </button>
                <button
                  onClick={handlePrint}
                  className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-400 hover:text-white"
                >
                  Print
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setShowMiscModal(true)}
                  className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-400 hover:text-white"
                >
                  Misc
                </button>
                <button
                  type="button"
                  onClick={() => setShowDiscountModal(true)}
                  className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:border-sky-400 hover:text-white"
                >
                  Discount
                </button>
                <button
                  onClick={() => {
                    if (totalItems === 0) return;
                    if (window.confirm('Delete current sale?')) {
                      clearCart();
                    }
                  }}
                  className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:border-red-400 hover:text-red-300"
                >
                  Delete Sale
                </button>
              </div>
              {preorderMode ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => submitPreorder(false)}
                    disabled={preorderSubmitting || cart.length === 0}
                    className="rounded-lg border border-emerald-600 bg-emerald-600/10 px-3 py-2 text-sm font-semibold text-emerald-200 transition hover:bg-emerald-600/20 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {preorderSubmitting ? 'Saving...' : 'Save Pre-Order'}
                  </button>
                  <button
                    type="button"
                    onClick={() => submitPreorder(true)}
                    disabled={preorderSubmitting || cart.length === 0}
                    className="rounded-lg bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-900 shadow-lg transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {preorderSubmitting ? 'Sending...' : 'Save & Send Invoice'}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPreorderModal(true)}
                    disabled={cart.length === 0}
                    className="rounded-lg border border-emerald-600 bg-emerald-600/10 px-3 py-2 text-sm font-semibold text-emerald-200 transition hover:bg-emerald-600/20 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Pre-Order
                  </button>
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="col-span-2 rounded-lg bg-emerald-500 px-3 py-3 text-base font-semibold text-slate-900 shadow-lg transition hover:bg-emerald-400"
                  >
                    Pay {formatCurrency(totalAmount, currencyCode)}
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
      {/*  Discount Modal  */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-bold mb-4">Apply Discount</h3>
            <div className="mb-2">
              <label className="mr-2">
                <input
                  type="radio"
                  name="type"
                  checked={discountType === 'percent'}
                  onChange={() => setDiscountType('percent')}
                />{' '}
                % Percentage
              </label>
              <label className="ml-4">
                <input
                  type="radio"
                  name="type"
                  checked={discountType === 'flat'}
                  onChange={() => setDiscountType('flat')}
                />{' '}
                 Flat
              </label>
            </div>
            <input
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
              className="border px-4 py-2 w-full mb-4"
              placeholder={
                discountType === 'percent' ? 'Enter % discount' : 'Enter flat discount'
              }
            />
            <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
              <input
                type="checkbox"
                checked={excludeDiscountedFromBasket}
                onChange={(e) => setExcludeDiscountedFromBasket(e.target.checked)}
              />
              Exclude items that already have a discount
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowDiscountModal(false)} className="px-4 py-2">
                Cancel
              </button>
              <button
                onClick={applyDiscount}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  Enhanced Payment Screen  */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="relative w-full max-w-5xl bg-slate-900 text-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
            <button
              onClick={() => {
                setShowPaymentModal(false);
                setPaymentAmount('');
              }}
              className="absolute top-4 right-4 text-2xl text-slate-400 hover:text-white"
              aria-label="Close payment"
            >
              &times;
            </button>

            {/* Summary column */}
            <div className="md:w-1/3 bg-slate-950/70 p-6 space-y-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Amount Due</p>
                <p className="text-4xl font-semibold mt-2">
                  {formatCurrency(totalAmount, currencyCode)}
                </p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Paid so far</span>
                  <span>{formatCurrency(amountPaid, currencyCode)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Current tender</span>
                  <span>{formatCurrency(currentTender, currencyCode)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">
                    {remainingBalance > 0 ? 'Remaining' : remainingBalance < 0 ? 'Change due' : 'Balance'}
                  </span>
                  <span className={remainingBalance > 0 ? 'text-amber-300' : 'text-emerald-300'}>
                    {remainingBalance === 0
                      ? formatCurrency(0, currencyCode)
                      : formatCurrency(Math.abs(remainingBalance), currencyCode)}
                  </span>
                </div>
              </div>

              {payments.length > 0 && (
                <div className="border border-slate-800 rounded-lg p-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                    Tender breakdown
                  </p>
                  <ul className="space-y-1 text-sm">
                    {payments.map((p, idx) => (
                      <li
                        key={`${p.method}-${idx}`}
                        className="flex justify-between text-slate-200"
                      >
                        <span>{p.method}</span>
                        <span>{formatCurrency(p.amount, currencyCode)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border border-slate-800 rounded-lg p-3 text-sm space-y-3">
                <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-slate-500">
                  <span>Totals</span>
                  <button
                    type="button"
                    onClick={() => setShowDiscountModal(true)}
                    className="text-xs font-semibold text-sky-400 hover:text-sky-300 transition"
                  >
                    Basket discount
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Items</span>
                      <span className="text-slate-200">{formatCurrency(totalBeforeDiscount, currencyCode)}</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {totalItems} item{totalItems === 1 ? '' : 's'}
                    </p>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Total discount</span>
                      <span>{formatCurrency(totalDiscount, currencyCode)}</span>
                    </div>
                    {excludeDiscountedFromBasket && (
                      <p className="text-[11px] text-slate-500">
                        Basket discount ignores items with item discounts.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Tax</span>
                      <span>{formatCurrency(taxTotal, currencyCode)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Subtotal</span>
                      <span className="text-emerald-300 font-semibold">{formatCurrency(totalAmount, currencyCode)}</span>
                    </div>
                  </div>
                </div>
                <div className="pt-2 text-xs text-slate-500 space-y-1 border-t border-slate-800">
                  <div className="flex justify-between">
                    <span>Item discounts</span>
                    <span>{formatCurrency(itemDiscountSum, currencyCode)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Basket discount</span>
                    <span>{formatCurrency(basketDiscount, currencyCode)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Keypad + actions */}
            <div className="flex-1 p-6 md:p-8 flex flex-col lg:flex-row gap-8">
              <div className="flex-1 flex flex-col gap-6">
                <div className="bg-slate-800/70 border border-slate-700 rounded-2xl px-6 py-5 text-right">
                  <p className="text-xs uppercase tracking-[0.4em] text-slate-400 mb-1">Tender</p>
                  <p className="text-4xl font-mono">
                    {formatCurrency(currentTender, currencyCode)}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3 text-2xl font-semibold">
                  {['1','2','3','4','5','6','7','8','9','.','0','?'].map((val) => (
                    <button
                      key={val}
                      onClick={() => {
                        if (val === '?') {
                          deleteLastDigit();
                        } else {
                          appendDigit(val);
                        }
                      }}
                      className="h-16 rounded-2xl bg-slate-800 border border-slate-700 hover:bg-slate-700 transition"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-full lg:w-56 flex flex-col gap-4">
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
                  {quickTenderAmounts.map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setQuickAmount(amt)}
                      className="h-16 rounded-2xl border border-slate-700 bg-slate-800 hover:bg-slate-700 text-lg font-semibold"
                    >
                      {formatCurrency(amt, currencyCode)}
                    </button>
                  ))}
                  <button
                    onClick={() => setPaymentAmount('')}
                    className="h-16 rounded-2xl border border-slate-700 bg-slate-800 hover:bg-slate-700 text-lg font-semibold"
                  >
                    Clear
                  </button>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => handlePayment('Cash')}
                    className="w-full h-16 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-lg font-semibold transition"
                  >
                    Take Cash
                  </button>
                  <button
                    onClick={() => handlePayment('Card')}
                    className="w-full h-16 rounded-2xl bg-blue-500 hover:bg-blue-400 text-lg font-semibold transition"
                  >
                    Take Card
                  </button>
                  <button
                    onClick={() => handlePayment('Other')}
                    className="w-full h-16 rounded-2xl bg-slate-700 hover:bg-slate-600 text-lg font-semibold transition"
                  >
                    Other Method
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreorderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Pre-Order</p>
                <h3 className="text-xl font-semibold text-white">Save cart as pre-order</h3>
                <p className="text-sm text-slate-400">
                  Collect contact details and hold this cart as a pre-order.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPreorderModal(false)}
                className="rounded-full border border-slate-700 px-3 py-1 text-xs uppercase tracking-wide text-slate-300 hover:border-slate-500"
              >
                Close
              </button>
            </div>

            <div className="mt-4 grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm text-slate-200">
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Customer name</span>
                  <input
                    type="text"
                    value={preorderForm.name}
                    onChange={(e) => setPreorderForm((f) => ({ ...f, name: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  />
                </label>
                <PhoneInput
                  label="Phone / WhatsApp"
                  value={preorderForm.phone}
                  onChange={(value) => setPreorderForm((f) => ({ ...f, phone: value }))}
                  wrapperClassName="text-sm text-slate-200"
                  placeholder="e.g. 7900 999 000"
                />
              </div>
              <label className="text-sm text-slate-200">
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Email (optional)</span>
                <input
                  type="email"
                  value={preorderForm.email}
                  onChange={(e) => setPreorderForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </label>
              <label className="text-sm text-slate-200">
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Address (optional)</span>
                <input
                  type="text"
                  value={preorderForm.address}
                  onChange={(e) => setPreorderForm((f) => ({ ...f, address: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </label>
              <label className="text-sm text-slate-200">
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Notes (optional)</span>
                <textarea
                  value={preorderForm.notes}
                  onChange={(e) => setPreorderForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </label>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-slate-400">
                {cart.length} item{cart.length === 1 ? '' : 's'} |{' '}
                {formatCurrency(totalAmount, currencyCode)}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowPreorderModal(false)}
                  className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-slate-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitPreorder}
                  disabled={preorderSubmitting}
                  className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {preorderSubmitting ? 'Saving...' : 'Save as Pre-Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/*  Item Discount Modal  */}
      {showItemDiscountModal && currentItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-bold mb-4">Discount for {currentItem.name}</h3>
            <div className="mb-2">
              <label className="mr-2">
                <input
                  type="radio"
                  name="itemType"
                  checked={itemDiscountType === 'percent'}
                  onChange={() => setItemDiscountType('percent')}
                />{' '}
                % Percentage
              </label>
              <label className="ml-4">
                <input
                  type="radio"
                  name="itemType"
                  checked={itemDiscountType === 'flat'}
                  onChange={() => setItemDiscountType('flat')}
                />{' '}
                 Flat
              </label>
            </div>
            <input
              type="number"
              value={itemDiscountValue}
              onChange={(e) => setItemDiscountValue(parseFloat(e.target.value) || 0)}
              className="border px-4 py-2 w-full mb-4"
              placeholder={
                itemDiscountType === 'percent'
                  ? 'Enter % discount'
                  : 'Enter flat discount'
              }
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowItemDiscountModal(false)}
                className="px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={applyItemDiscount}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  Item Note Modal  */}
      {showItemNoteModal && currentItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-bold mb-4">Note for {currentItem.name}</h3>
            <textarea
              value={itemNoteText}
              onChange={(e) => setItemNoteText(e.target.value)}
              className="border w-full h-24 p-2 mb-4"
              placeholder="Enter note..."
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowItemNoteModal(false)}
                className="px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={applyItemNote}
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  Receipt Modal  */}
      {showReceipt && (
        <div
          className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 px-4"
          onClick={closeReceiptModal}
        >
          <div
            className="relative w-full max-w-3xl bg-slate-900 text-white rounded-3xl shadow-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeReceiptModal}
              className="absolute top-5 right-6 text-2xl text-slate-400 hover:text-white"
              aria-label="Close receipt"
            >
              &times;
            </button>

            <div className="space-y-6">
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Sale complete</p>
                <h2 className="text-2xl font-semibold mt-2">Deliver receipt</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Capture customer contact details and choose how you want to share this receipt.
                </p>
              </div>

              <div className="space-y-5 rounded-3xl border border-slate-800 bg-slate-900/50 p-6">
                <div className="space-y-4">
                  <label className="space-y-2 text-sm text-slate-300">
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500">
                      Select customer
                    </span>
                    <div className="relative">
                      <input
                        type="text"
                        value={customerSelectQuery}
                        onChange={(e) => {
                          setCustomerSelectQuery(e.target.value);
                          setShowCustomerSuggestions(true);
                        }}
                        onFocus={() => setShowCustomerSuggestions(true)}
                        autoComplete="new-customer-search"
                        name="receiptCustomerSearch"
                        className="w-full rounded-2xl border border-slate-700 bg-slate-800/80 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                        placeholder="Search saved customers"
                      />
                      {showCustomerSuggestions && customerSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-2xl border border-slate-700 bg-slate-950/95 text-sm shadow-xl">
                          {customerSuggestions.map((customer) => (
                            <button
                              type="button"
                              key={customer.id}
                              className="flex w-full flex-col gap-0.5 px-4 py-2 text-left text-slate-200 hover:bg-slate-900"
                              onClick={() => {
                                setReceiptCustomerName(customer.name || '');
                                setReceiptEmail(customer.email || '');
                                setReceiptPhone(customer.phone || '');
                                setCustomerSelectQuery(customer.name || '');
                                setShowCustomerSuggestions(false);
                                setActiveCustomer(customer);
                                setMarketingOptIn(Boolean(customer.marketingOptIn));
                                setWhatsappOptIn(Boolean(customer.whatsappOptIn));
                                setSmsOptIn(Boolean(customer.smsOptIn));
                                setCustomerNotes(customer.notes || '');
                              }}
                            >
                              <span className="font-semibold">{customer.name || 'Unnamed customer'}</span>
                              <span className="text-xs text-slate-400">
                                {customer.email || customer.phone || 'No contact info'}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-2 text-sm text-slate-300">
                      <span className="text-xs uppercase tracking-[0.2em] text-slate-500">
                        Customer name (optional)
                      </span>
                      <input
                        type="text"
                        value={receiptCustomerName}
                        onChange={(e) => setReceiptCustomerName(e.target.value)}
                        autoComplete="new-customer-name"
                        name="receiptCustomerName"
                        className="w-full rounded-2xl border border-slate-700 bg-slate-800/80 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                        placeholder="e.g. Hannah"
                      />
                    </label>
                    <label className="space-y-2 text-sm text-slate-300">
                      <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Email address</span>
                      <input
                        type="email"
                        value={receiptEmail}
                        onChange={(e) => setReceiptEmail(e.target.value)}
                        onKeyDown={handleReceiptEmailKeyDown}
                        className="w-full rounded-2xl border border-slate-700 bg-slate-800/80 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                        placeholder="customer@example.com"
                        autoComplete="new-customer-email"
                        name="receiptCustomerEmail"
                      />
                    </label>
                    <label className="space-y-2 text-sm text-slate-300">
                      <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Phone (optional)</span>
                      <input
                        type="tel"
                        value={receiptPhone}
                        onChange={(e) => setReceiptPhone(e.target.value)}
                        className="w-full rounded-2xl border border-slate-700 bg-slate-800/80 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                        placeholder="+44 7123 456 789"
                        autoComplete="new-customer-phone"
                        name="receiptCustomerPhone"
                      />
                      <p className="text-xs text-slate-500">
                        Used for future WhatsApp/SMS receipts or customer follow-ups.
                      </p>
                    </label>
                  </div>
                </div>

                <label className="mt-2 flex items-start gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={rememberCustomerNextSale}
                    onChange={(e) => setRememberCustomerNextSale(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                  />
                  <div>
                    <p className="font-semibold text-white">Remember customer details</p>
                    <p className="text-xs text-slate-500">
                      Prefill the next receipt modal with this customer&apos;s info.
                    </p>
                  </div>
                </label>

                {activeCustomer && (
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                        Saved profile preferences
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveCustomer(null);
                          setMarketingOptIn(false);
                          setWhatsappOptIn(false);
                          setSmsOptIn(false);
                          setCustomerNotes('');
                          setCustomerSelectQuery('');
                        }}
                        className="text-xs text-slate-400 underline"
                      >
                        Clear selection
                      </button>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3">
                      <label className="flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-[11px] uppercase tracking-wide">
                        <input
                          type="checkbox"
                          checked={marketingOptIn}
                          onChange={(e) => setMarketingOptIn(e.target.checked)}
                        />
                        Email opt-in
                      </label>
                      <label className="flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-[11px] uppercase tracking-wide">
                        <input
                          type="checkbox"
                          checked={whatsappOptIn}
                          onChange={(e) => setWhatsappOptIn(e.target.checked)}
                        />
                        WhatsApp opt-in
                      </label>
                      <label className="flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-[11px] uppercase tracking-wide">
                        <input
                          type="checkbox"
                          checked={smsOptIn}
                          onChange={(e) => setSmsOptIn(e.target.checked)}
                        />
                        SMS opt-in
                      </label>
                    </div>
                    <textarea
                      value={customerNotes}
                      onChange={(e) => setCustomerNotes(e.target.value)}
                      rows={3}
                      placeholder="Add internal notes (allergies, VIP status, etc.)"
                      className="w-full rounded-2xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!activeCustomer) return;
                        setSavingCustomerPrefs(true);
                        try {
                          const res = await api.put(`/customers/${activeCustomer.id}`, {
                            name: receiptCustomerName || undefined,
                            email: receiptEmail || null,
                            phone: receiptPhone || null,
                            marketingOptIn,
                            whatsappOptIn,
                            smsOptIn,
                            notes: customerNotes || null,
                          });
                          setActiveCustomer(res.data);
                          toast.success('Customer preferences saved.');
                        } catch (err) {
                          toast.error(err.response?.data?.error || 'Failed to update customer.');
                        } finally {
                          setSavingCustomerPrefs(false);
                        }
                      }}
                      disabled={savingCustomerPrefs}
                      className="rounded-2xl border border-slate-700 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 disabled:opacity-60"
                    >
                      {savingCustomerPrefs ? 'Saving…' : 'Save customer preferences'}
                    </button>
                  </div>
                )}

                <div className="grid gap-3 md:grid-cols-2">
                  {[
                    {
                      key: 'print',
                      label: 'Ticket printer',
                      description: printReceiptEnabled
                        ? 'Receipt will print automatically.'
                        : 'Printing is disabled for this sale.',
                      enabled: printReceiptEnabled,
                      toggle: () => setPrintReceiptEnabled((prev) => !prev),
                    },
                    {
                      key: 'email',
                      label: 'Email receipt',
                      description: emailReceiptEnabled
                        ? 'We will email the receipt to this customer.'
                        : 'Email will not be sent.',
                      enabled: emailReceiptEnabled,
                      toggle: () => setEmailReceiptEnabled((prev) => !prev),
                    },
                    {
                      key: 'whatsapp',
                      label: 'WhatsApp receipt',
                      description: whatsappReceiptEnabled
                        ? 'Share via WhatsApp instantly.'
                        : 'WhatsApp delivery disabled.',
                      enabled: whatsappReceiptEnabled,
                      toggle: () => setWhatsappReceiptEnabled((prev) => !prev),
                    },
                    {
                      key: 'sms',
                      label: 'SMS receipt',
                      description: smsReceiptEnabled
                        ? 'Send a text receipt to the customer.'
                        : 'SMS delivery disabled.',
                      enabled: smsReceiptEnabled,
                      toggle: () => setSmsReceiptEnabled((prev) => !prev),
                    },
                  ].map(({ key, label, description, enabled, toggle }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={toggle}
                      className={`rounded-2xl border px-5 py-4 text-left transition ${
                        enabled
                          ? 'border-emerald-400 bg-emerald-500/15 text-white shadow-lg shadow-emerald-500/20'
                          : 'border-slate-700 bg-slate-800/60 text-slate-300 hover:border-slate-500'
                      }`}
                    >
                      <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em]">
                        <span>{label}</span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            enabled ? 'bg-emerald-400/20 text-emerald-200' : 'bg-slate-700 text-slate-400'
                          }`}
                        >
                          {enabled ? 'ON' : 'OFF'}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-300">{description}</p>
                    </button>
                  ))}
                </div>

                <p className="text-xs text-slate-500">
                  No delivery method selected? We will still record the sale and you can resend the receipt later.
                </p>
              </div>

              {receiptDeliveryError && (
                <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                  {receiptDeliveryError}
                </div>
              )}

              {completedReceipt && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowReceiptDetails((prev) => !prev)}
                    className="text-sm text-slate-300 underline underline-offset-4"
                  >
                    {showReceiptDetails ? 'Hide receipt summary' : 'View receipt summary'}
                  </button>

                  {showReceiptDetails && (
                    <div className="rounded-2xl border border-slate-800 bg-slate-800/60 p-6 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-slate-300">
                        <div>
                          <p>Receipt #{completedReceipt.id}</p>
                          <p>{new Date(completedReceipt.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="mt-3 sm:mt-0 text-right">
                          <p>Payment: {completedReceipt.paymentMethod}</p>
                          <p>Total Discount: {formatCurrency(completedReceipt.totalDiscount, currencyCode)}</p>
                        </div>
                      </div>
                      <div className="bg-slate-900/60 rounded-xl divide-y divide-slate-800">
                        {completedReceipt.items.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between px-4 py-2 text-sm">
                            <div>
                              <p className="font-medium text-slate-100">{item.productName}</p>
                              <p className="text-xs text-slate-400">
                                {item.quantity} x {formatCurrency(Number(item.unitPrice || 0), currencyCode)}
                              </p>
                            </div>
                            <span className="text-sm font-semibold text-slate-100">
                              {formatCurrency(Number(item.subtotal || 0), currencyCode)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end text-lg font-semibold">
                        <span>{formatCurrency(completedReceipt.total, currencyCode)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={handleSkipReceiptDelivery}
                  className="w-full rounded-2xl border border-slate-700 bg-slate-800/60 px-5 py-3 text-sm font-semibold text-slate-200 transition hover:border-slate-500 sm:w-auto"
                >
                  Skip receipt delivery
                </button>
                <button
                  type="button"
                  onClick={handleReceiptDelivery}
                  disabled={sendingReceiptEmail}
                  className="w-full rounded-2xl bg-emerald-500 px-6 py-3 text-base font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                >
                  {sendingReceiptEmail ? 'Sending...' : getReceiptDeliveryCtaLabel()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/*  Held Sales Modal  */}
      {showHeldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-40">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Held Sales</h3>
            {heldSales.length > 0 ? (
              <ul className="space-y-3 max-h-64 overflow-y-auto">
                {heldSales.map((held) => (
                  <li key={held.id} className="border rounded p-3 flex flex-col">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold">Sale #{held.id}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(held.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Items: {held.cartData.length}</span>
                      <span>Total: {formatCurrency(getHeldTotal(held.cartData), currencyCode)}</span>
                    </div>
                    <div className="flex gap-2 mt-auto">
                      <button
                        onClick={() => resumeSale(held)}
                        className="flex-1 bg-green-600 text-white px-3 py-1 rounded text-sm"
                      >
                        Resume
                      </button>
                      <button
                        onClick={() => deleteHeldSale(held)}
                        className="flex-1 bg-red-600 text-white px-3 py-1 rounded text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-center text-gray-600">No held sales available.</p>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowHeldModal(false)}
                className="text-sm underline"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/*  Misc Product Modal  */}
      {showMiscModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Add Misc Product</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1" htmlFor="miscName">
                Item Name
              </label>
              <input
                id="miscName"
                type="text"
                value={miscName}
                onChange={(e) => setMiscName(e.target.value)}
                className="border rounded w-full px-3 py-2"
                placeholder="Enter item description"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1" htmlFor="miscPrice">
                Price
              </label>
              <input
                id="miscPrice"
                type="number"
                min="0"
                step="0.01"
                value={miscPrice}
                onChange={(e) => setMiscPrice(e.target.value)}
                className="border rounded w-full px-3 py-2"
                placeholder={`Enter price (${currencyCode})`}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowMiscModal(false);
                  setMiscName('');
                  setMiscPrice('');
                }}
                className="px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMiscItem}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function IconNote({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M9 13h6" />
      <path d="M9 17h3" />
    </svg>
  );
}

function IconPercent({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="7.5" cy="7.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
    </svg>
  );
}

function IconTrash({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 6h18" />
      <path d="M8 6v-2a2 2 0 012-2h4a2 2 0 012 2v2" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

